import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import icons from '../../../../constants/icons';
import {
  getStepIcon,
  STEP_LABEL_KEYS,
  type StepRunState,
} from './steamGameImportTypes';

export interface SteamGameImportProgressCardProps {
  isComplete: boolean;
  isImporting: boolean;
  progress: number;
  steps: StepRunState[];
  fetchedGamesCount: number;
  currentOperationLabel: string;
}

function SteamGameImportProgressCard({
  isComplete,
  isImporting,
  progress,
  steps,
  fetchedGamesCount,
  currentOperationLabel,
}: SteamGameImportProgressCardProps) {
  const { t } = useTranslation();

  return (
    <div className="card glass-panel p-4 mb-3 flex-shrink-0">
      <div className="d-flex justify-content-between align-items-end mb-3">
        <div>
          <h5 className="mb-1">
            {isComplete ? (
              <span className="text-success">
                <FontAwesomeIcon icon={icons.CIRCLE_CHECK} className="me-2" />
                {t('Sync Complete')}
              </span>
            ) : (
              <span>
                <FontAwesomeIcon
                  icon={icons.SPINNER}
                  spin
                  className="text-blue me-2"
                />
                {t('Processing Steam Catalog...')}
              </span>
            )}
          </h5>
          <p className="small text-secondary mb-0">
            {fetchedGamesCount === 0 && isImporting
              ? t('Fetching from Steam API...')
              : t('Current Operation: {{label}}', {
                  label: currentOperationLabel,
                })}
          </p>
        </div>
        <div className="text-end">
          <span className="display-6 fw-bold text-blue">{progress}%</span>
        </div>
      </div>

      <div
        className="progress bg-dark mb-4"
        style={{
          height: '12px',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <motion.div
          className="progress-bar bg-cyan progress-shimmer"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
        />
      </div>

      <div className="row g-3">
        {steps.map((step) => (
          <div className="col-md-6 col-xl-4" key={step.id}>
            <div
              className={`card bg-transparent p-3 step-card ${step.status === 'active' ? 'active' : step.status === 'complete' ? 'complete' : ''}`}
            >
              <div className="d-flex align-items-center">
                <div
                  className="me-3 rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    flexShrink: 0,
                    background:
                      step.status === 'complete'
                        ? '#10b98122'
                        : 'rgba(255,255,255,0.05)',
                  }}
                >
                  <FontAwesomeIcon
                    icon={icons[getStepIcon(step.id)]}
                    className={`${step.status === 'complete' ? 'text-success' : step.status === 'active' ? 'text-blue' : 'text-secondary'}`}
                  />
                </div>
                <div className="small">{t(STEP_LABEL_KEYS[step.id] ?? '')}</div>
                {step.status === 'complete' && (
                  <FontAwesomeIcon
                    icon={icons.CHECK}
                    className="text-success ms-auto"
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SteamGameImportProgressCard;
